/**
 * Apply server-action field/form errors to a react-hook-form.
 *
 * Bridges our `ServerActionResult` error shape onto RHF's `setError` API so
 * form components don't have to re-implement the mapping inline.
 *
 * - Per-field errors (`error.field` set, not `_form`) attach to that RHF
 *   field and the first matching field is auto-focused on submit thanks to
 *   RHF's `shouldFocusError` default.
 * - Form-level errors (`error.field` missing or `"_form"`) are forwarded
 *   to the optional `onFormError` callback — typically the component
 *   stores it in `setError("root", ...)` and/or toasts it.
 */
import type { FieldError, ServerActionResult } from "@/types/central";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

interface ApplyOptions {
  onFormError?: (message: string) => void;
}

/**
 * Error thrown by hooks that wrap a server action (e.g. `useUpdateUser`) so
 * the structured `ServerActionResult` survives the promise rejection path.
 * Consumers can unwrap `error.result` and pass it to `applyServerErrors`
 * instead of losing field-level detail behind an opaque `new Error(message)`.
 */
export class ServerActionError<T = unknown> extends Error {
  readonly result: ServerActionResult<T>;

  constructor(result: ServerActionResult<T>) {
    const first =
      !result.success && result.errors && result.errors.length > 0
        ? result.errors[0]
        : undefined;
    super(
      (!result.success && result.message) ||
        first?.message ||
        "Server action failed",
    );
    this.name = "ServerActionError";
    this.result = result;
  }
}

export function isServerActionError<T = unknown>(
  error: unknown,
): error is ServerActionError<T> {
  return error instanceof ServerActionError;
}

/**
 * Unwrap a {@link ServerActionResult} for TanStack Query mutationFns.
 * Failure results throw {@link ServerActionError} so `onError` still fires
 * and the backend message survives to the client.
 */
export function unwrapServerActionData<T>(
  result: ServerActionResult<T>,
  missingDataMessage = "Server action succeeded without data",
): T {
  if (!result.success) {
    throw new ServerActionError(result);
  }
  if (result.data === undefined) {
    throw new ServerActionError({
      success: false,
      errors: [{ message: missingDataMessage, type: "server" }],
    });
  }
  return result.data;
}

/** Like {@link unwrapServerActionData} for void actions (delete, etc.). */
export function assertServerActionSuccess(
  result: ServerActionResult<unknown>,
): void {
  if (!result.success) {
    throw new ServerActionError(result);
  }
}

export function applyServerErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  result: ServerActionResult<unknown>,
  options: ApplyOptions = {},
): void {
  if (result.success) return;
  if (!result.errors || !Array.isArray(result.errors)) {
    options.onFormError?.("An error occurred. Please try again.");
    return;
  }

  let sawFormError = false;
  let sawFieldError = false;

  for (const err of result.errors as FieldError[]) {
    const message = err.message ?? "Invalid value";
    // Forward the backend's error kind (validation / permission / auth /
    // network / server) so downstream UI can style or branch on `type`.
    // Default to "server" when the backend omits it.
    const type = err.type ?? "server";
    if (err.field && err.field !== "_form") {
      setError(err.field as Path<T>, { type, message });
      sawFieldError = true;
    } else {
      options.onFormError?.(message);
      sawFormError = true;
    }
  }

  if (!sawFormError && !sawFieldError) {
    options.onFormError?.("An error occurred. Please try again.");
  }
}
